<template>
  <v-form ref="form" @submit.prevent="login">
    <!-- Contact BYN -->
     <div class="text-center mt-6">
      <p class="mb-6">{{ $t('login.password.problems.title') }}</p>
     </div>

    <v-text-field
      v-model.trim="email"
      outlined
      :label="$t('login.password.email')"
      :rules="[rules.required, rules.email]"
      :disabled="isLoading"
      @input="clearErrorMessages"
    />

    <div v-if="capsIsOn" class="caps-lock">
      <span>{{ $t('login.password.caps-lock') }}</span>
    </div>

    <v-text-field
      v-model="password"
      outlined
      :type="showPassword ? 'text' : 'password'"
      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
      :label="$t('login.password.password')"
      :rules="[rules.required]"
      :disabled="isLoading"
      :error-messages="error"
      class="mb-0 mt-n4"
      @click:append="showPassword = !showPassword"
      @keydown="checkCapsLock"
      @keyup="checkCapsLock"
      @input="clearErrorMessages"
    />

    <div class="d-flex mb-5">
      <button
        type="button"
        class="text-decoration-underline ml-auto"
        @click="showRequestSmsModal"
      >
        {{ $t('login.password.forgot-password') }}
      </button>
    </div>

    <!-- Password Reset Flow Modals -->
    <ForgotPasswordModal
      :show="showForgotPasswordModal"
      @close="closeRequestSmsModal"
    />

    <SmsCodeModal
      :show="showSmsCodeModal"
      :mobilephone="mobilephone"
      :lastSms="lastSmsSentAt"
      @close="closeSmsCodeModal"
      @sendSms="updateSmsTimestamp"
    />

    <NewPasswordModal
      :show="showNewPasswordModal"
      :resetToken="resetToken"
      @close="closeNewPasswordModal"
    />

    <RestoreSuccessModal
      :show="showRestoreSuccessModal"
      @close="closeRestoreSuccessModal"
    />

    <v-btn
      class="login-email login-email--primary v-btn--full-width"
      block
      outlined
      :loading="isLoading"
      :disabled="isLoading"
      @click="login"
    >
      <div class="login-email__content">
        <span>{{ $t('login.password.log-in') }}</span>
      </div>
    </v-btn>
  </v-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import API from '@/services/api.js'
import { RulesMixin } from '@/components/mixins'

import ForgotPasswordModal from '@/components/Start/RestorePassword/ForgotPasswordModal'
import SmsCodeModal from '@/components/Start/RestorePassword/SmsCodeModal'
import NewPasswordModal from '@/components/Start/RestorePassword/NewPasswordModal'
import RestoreSuccessModal from '@/components/Start/RestorePassword/RestoreSuccessModal'

export default {
  name: 'EmailLogin',

  components: {
    ForgotPasswordModal,
    SmsCodeModal,
    NewPasswordModal,
    RestoreSuccessModal
  },

  mixins: [RulesMixin],

  data: () => ({
    // Auth State
    email: '',
    password: '',
    showPassword: false,
    isLoading: false,
    error: '',
    capsIsOn: false,

    // Password Restore Flow State
    mobilephone: '',
    lastSmsSentAt: '',
    resetToken: '',
    showForgotPasswordModal: false,
    showSmsCodeModal: false,
    showNewPasswordModal: false,
    showRestoreSuccessModal: false
  }),

  computed: {
    ...mapGetters('currentuser', ['isLoggedIn'])
  },

  created() {
    if (this.isLoggedIn) {
      this.$router.push({ name: 'Start' })
    }
  },

  methods: {
    ...mapActions('currentuser', ['setCurrentUser']),

    // Authentication
    async login() {
      if (!this.$refs.form.validate()) return

      this.isLoading = true
      this.clearErrorMessages()

      try {
        const { data } = await API.passwordLogin({
          email: this.email,
          password: this.password
        })

        await this.setCurrentUser(data)
        this.$router.push({ name: 'Start' })
      } catch (err) {
        this.error =
          err?.response?.data ||
          this.$t('login.password.error-fallback') ||
          'Fel vid försök till inloggning.'
      } finally {
        this.isLoading = false
      }
    },

    checkCapsLock(e) {
      if (e && typeof e.getModifierState === 'function') {
        this.capsIsOn = e.getModifierState('CapsLock')
      }
    },

    clearErrorMessages() {
      if (this.error) this.error = ''
    },

    // Password Reset Flow
    showRequestSmsModal() {
      this.showForgotPasswordModal = true
    },

    closeRequestSmsModal(result) {
      this.showForgotPasswordModal = false
      if (!result) return

      this.mobilephone = result.mobilephone
      this.lastSmsSentAt = result.sentTime
      this.showSmsCodeModal = true
    },

    closeSmsCodeModal(result) {
      this.showSmsCodeModal = false
      if (!result) return

      this.resetToken = result.token
      this.showNewPasswordModal = true
    },

    closeNewPasswordModal(result) {
      this.showNewPasswordModal = false
      if (!result) return

      this.showRestoreSuccessModal = true
    },

    closeRestoreSuccessModal() {
      this.$refs.form?.reset()
      this.showRestoreSuccessModal = false
    },

    updateSmsTimestamp(timestamp) {
      this.lastSmsSentAt = timestamp
    }
  }
}
</script>

<style lang="scss" scoped>
.caps-lock {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  span {
    background-color: rgba(0, 0, 0, 0.75);
    color: #fff;
    height: 24px;
    border-radius: 4px;
    padding: 2px 10px;
    position: absolute;
    top: -26px;
  }
}

.login-email {
  height: 56px !important;
  border-radius: 56px !important;
  text-transform: none !important;
  font-size: 14px !important;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.1px;
  box-shadow: none !important;

  &__content {
    width: 100%;
  }

  &--primary {
    text-align: center !important;
    margin-bottom: 20px;
    background-color: $color-primary !important;
    color: #fff !important;

    &:hover {
      background-color: #00747b !important;
    }
  }
}
</style>